import type { StoryEditChange } from 'common/story-edit-operations/types';
import type { EditCaptureResult } from './save-capture';

/**
 * What a load changes without the user: the story as persisted becomes the story as loaded (migration,
 * processing, app version), and a card becomes the card as the editor holds it (default card settings,
 * content height). It is not an edit, so it is never an action of its own: it is held and sent ahead of
 * the changes of the first action that has changes, as autosave persists it with its first save.
 */
export function createHeldNormalization() {
	let held: { storyId: string; changes: StoryEditChange[] } | undefined;
	return {
		/** A story load starts over: the loaded story replaces whatever was held. */
		replace(storyId: string, changes: StoryEditChange[]) {
			held = { storyId, changes: [...changes] };
		},
		/** A card load adds to the normalization of its story. */
		add(storyId: string, changes: StoryEditChange[]) {
			if (held?.storyId !== storyId) held = { storyId, changes: [] };
			held.changes.push(...changes);
		},
		drop() {
			held = undefined;
		},
		/** Puts the held normalization ahead of the first action with changes; any other result passes as is. */
		attach(storyId: string, capture: EditCaptureResult): EditCaptureResult {
			if (capture.status !== 'captured' || held?.storyId !== storyId || !held.changes.length) return capture;
			const changes = [...held.changes, ...capture.changes];
			held = undefined;
			return { status: 'captured', changes };
		},
	};
}
